import classNames from "classnames";
import { PopupNodeProps } from "../BuilderInterface";
import "./style.scss";
import { getCompoundStyle } from "../helpers";

import { useEffect, useRef, useState } from "react";
import DynamicIcon from "../DynamicIcon";
import { PopupAlignment, PopupNode } from "../../build/types";

const getButtonWidth = (iconWidth?: string, padding?: string, borderWidth?: string) => {
    let acc = [];
    if (iconWidth) {
        acc.push(iconWidth);
    } else {
        acc.push("50px");
    }

    if (padding) {
        const paddingSplit = padding.split(" ");
        acc.push([paddingSplit[1], paddingSplit[3]].join(" + "));
    } else {
        acc.push("20px");
    }

    if (borderWidth) {
        const borderSplit = borderWidth.split(" ");
        acc.push([borderSplit[1], borderSplit[3]].join(" + "));
    }

    if (acc.length > 0) {
        return acc.join(" + ");
    }

    return "";
};

const BuilderPopup: React.FC<PopupNodeProps> = ({ node, children, onBackdropClose, builderProps }: PopupNodeProps) => {
    const pNode = node as PopupNode;
    const { id, attr, closeButton, alignment, backdropClose } = pNode;
    const div = useRef<HTMLDivElement>(null);
    const builderPopupClass = classNames("builder-popup", {
        [`${attr?.className}`]: !!attr?.className
    });

    const backgroundClick = (e: React.MouseEvent) => {
        if (onBackdropClose) {
            if (e.currentTarget === e.target) {
                onBackdropClose();
            }
        }
    };

    //Close button
    const [maxWidth, setMaxWidth] = useState<string>("");
    const dependArray = [
        closeButton?.active,
        closeButton?.style?.padding,
        closeButton?.style?.borderWidth,
        closeButton?.icon?.style?.width,
        alignment
    ];
    useEffect(() => {
        if (closeButton?.active) {
            const newWidth = getButtonWidth(
                closeButton?.icon?.style?.width as string,
                closeButton?.style?.padding as string,
                closeButton?.style?.borderWidth as string
            );
            if (newWidth) {
                setMaxWidth(
                    `calc(100% - ((40px + ${newWidth}) * ${!alignment || alignment === PopupAlignment.CENTER ? "2" : "1"}))`
                );
            } else {
                setMaxWidth(`calc(100% - (90px *  ${!alignment || alignment === PopupAlignment.CENTER ? "2" : "1"}))`);
            }
        } else {
            setMaxWidth("");
        }
    }, dependArray);

    //Alignment
    const backgroundClass = classNames("builder-popup-background", {
        [`align-${alignment}`]: !!alignment
    });

    if (builderProps) {
        const { extraChild, selectedMenu, selectCallback, outlineCallback, onDragOverCapture } = builderProps;

        return (
            <div className={backgroundClass}>
                <div
                    {...attr}
                    ref={div}
                    className={builderPopupClass}
                    style={getCompoundStyle({ node, otherStyle: maxWidth ? { maxWidth } : undefined })}
                    onClick={e => (selectCallback ? selectCallback(e, id) : {})}
                    onMouseMove={e => (outlineCallback ? outlineCallback(e, id) : {})}
                    id={id}
                    onDragOverCapture={e => onDragOverCapture?.(e, id, div.current as HTMLDivElement)}
                >
                    {extraChild}
                    {children}
                    {selectedMenu}
                    {closeButton?.active && (
                        <button className="popup-exit" style={closeButton?.style}>
                            <DynamicIcon
                                name={closeButton?.icon?.src || "cross"}
                                colors={closeButton?.icon?.colors}
                                style={closeButton?.icon?.style}
                            />
                        </button>
                    )}
                </div>
            </div>
        );
    } else {
        return (
            <div
                className={backgroundClass}
                onClick={e => {
                    if (backdropClose) backgroundClick(e);
                }}
            >
                <div
                    {...attr}
                    ref={div}
                    className={builderPopupClass}
                    style={getCompoundStyle({ node, otherStyle: maxWidth ? { maxWidth } : undefined })}
                    id={id}
                >
                    {children}
                    {closeButton?.active && (
                        <button className="popup-exit" onClick={onBackdropClose} style={closeButton?.style}>
                            <DynamicIcon
                                name={closeButton?.icon?.src || "cross"}
                                colors={closeButton?.icon?.colors}
                                style={closeButton?.icon?.style}
                            />
                        </button>
                    )}
                </div>
            </div>
        );
    }
};

export default BuilderPopup;
